import React, { useEffect, useRef, useState } from "react";
import countries from "../../../countries.json";
import { elakayAutoText } from "../dataElakay";
import useAddressLocation from "./useAddressLocation";
import { postalKindOf, stateKindOf } from "./addressLocationData";
import "./AddressLocationSelector.css";

// The shared Country / ZIP-postal code / State / City fields of every Elakay
// address form (My Profile, associate activation, checkout, shops, billing,
// wholesale delivery, profile location, Target Location zones). The
// geographic logic lives in useAddressLocation; this component only renders
// it, so each form keeps its own layout (classNames), its own other fields
// and its own business rules.
//
// Renders the fields as siblings (no wrapper) unless `wrapperClassName` is
// given, so they drop straight into the caller's grid.
const cx = (...names) => names.filter(Boolean).join(" ");

const STATE_LABELS = {
  state: ["state", "State"],
  province: ["province", "Province"],
  region: ["region", "Region"],
  department: ["department", "Department"],
  parish: ["parish", "Parish"],
  district: ["district", "District"],
  generic: ["stateGeneric", "State / Province / Region"],
};

const AddressLocationSelector = ({
  value,
  onChange,
  strict = false,
  idPrefix = "address-location",
  disabled = false,
  wrapperClassName = "",
  classNames = {},
  labels = {},
  hints = {},
  errors = {},
  onBlurField,
  onRequirementsChange,
  emptyCountryLabel,
  countryOptionLabel,
  // Optional list of ISO codes when the form's business rules only allow
  // some countries (e.g. Stripe Connect accounts: US / CA).
  countryCodes,
  tr,
}) => {
  const loc = useAddressLocation({ value, onChange, strict });
  const v = loc.value;

  // Shared translations (h_addrloc_*), optionally overridden by the caller's
  // own section (Target Location keeps its localSelling texts).
  const text = (key, fallback, values) => {
    const own = elakayAutoText(`h_addrloc_${key}`, fallback, values);
    const custom = tr ? tr(key, own) : own;
    return values && custom !== own
      ? String(custom).replace(/\{([A-Za-z0-9_]+)\}/g, (m, name) => (values[name] ?? m))
      : custom;
  };

  useEffect(() => {
    onRequirementsChange?.({ postalRequired: loc.postalRequired, stateRequired: loc.stateRequired });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.postalRequired, loc.stateRequired]);

  // Strict mode: the typed city is only committed when it is in the list.
  const [cityInput, setCityInput] = useState(v.city);
  const lastCity = useRef(v.city);
  useEffect(() => { if (v.city !== lastCity.current) { lastCity.current = v.city; setCityInput(v.city); } }, [v.city]);

  const id = (name) => `${idPrefix}-${name}`;
  const blur = (name) => () => onBlurField?.(name);
  const inputClass = cx(classNames.input);
  const selectClass = cx(classNames.select || classNames.input);

  const countryLabel = labels.country || text("country", "Country");
  const stateKind = v.country ? stateKindOf(v.country) : "generic";
  const stateLabel = labels.state || (strict ? text("stateProvince", "State / Province") : text(...STATE_LABELS[stateKind]));
  const postalLabel = labels.postalCode || (strict
    ? text("zipCode", "ZIP code / Postal code")
    : !v.country ? text("zipOrPostal", "ZIP / Postal code")
      : postalKindOf(v.country) === "zip" ? text("zip", "ZIP code") : text("postal", "Postal code"));
  const cityLabel = labels.city || text("city", "City");
  const optional = <span className="alsel-optional"> {text("optionalTag", "(optional)")}</span>;

  const field = (name, label, control, { showOptional = false, idName = name } = {}) => (
    <div key={name} className={cx("alsel-field", `alsel-field--${name}`, classNames.field, classNames[`field_${name}`])}>
      <label htmlFor={id(idName)} className={classNames.label}>{label}{showOptional && optional}</label>
      {control}
      {errors[name]
        ? <p className={cx("alsel-error", classNames.error)} role="alert">{errors[name]}</p>
        : hints[name] ? <p className={cx("alsel-hint", classNames.hint)}>{hints[name]}</p> : null}
    </div>
  );

  // ── Country ──
  const countryControl = (
    <select id={id("country")} className={selectClass} value={v.country} disabled={disabled}
      autoComplete="country" onBlur={blur("country")}
      aria-invalid={errors.country ? "true" : "false"}
      onChange={(e) => { setCityInput(""); loc.setCountry(e.target.value); }}>
      <option value="">{emptyCountryLabel || text("selectCountry", "Select country")}</option>
      {(countryCodes ? countries.filter((c) => countryCodes.includes(c.code)) : countries).map((c) => <option key={c.code} value={c.code}>{countryOptionLabel ? countryOptionLabel(c) : c.name}</option>)}
    </select>
  );

  // ── State / province ──
  let stateControl;
  if (loc.freeText || (!strict && v.country && loc.states.loaded && !loc.hasStates)) {
    stateControl = (
      <input id={id("state")} className={inputClass} value={v.state || ""} disabled={disabled || !v.country}
        autoComplete="address-level1" onBlur={blur("state")} placeholder={strict ? text("optional", "Optional") : ""}
        aria-invalid={errors.state ? "true" : "false"}
        onChange={(e) => loc.setState(e.target.value)} />
    );
  } else {
    const legacy = v.state && !loc.stateItem ? v.state : "";
    stateControl = (
      <select id={id("state")} className={selectClass} value={loc.stateItem?.name || legacy}
        disabled={disabled || !v.country || loc.states.loading || (strict && !loc.hasStates)}
        autoComplete="address-level1" onBlur={blur("state")}
        aria-invalid={errors.state || loc.mismatchStates.length ? "true" : "false"}
        onChange={(e) => { if (strict) setCityInput(""); loc.setState(e.target.value); }}>
        <option value="">{
          !v.country ? text("selectCountryFirst", "Select a country first")
            : loc.states.loading ? text("loadingPlaces", "Loading…")
              : loc.hasStates ? text("selectState", "Select state / province")
                : text("noStates", "No states / provinces for this country")
        }</option>
        {legacy && <option value={legacy}>{legacy}</option>}
        {loc.states.items.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
      </select>
    );
  }

  // ── City ──
  let cityControl;
  if (loc.freeText) {
    cityControl = (
      <input id={id("city")} className={inputClass} value={v.city || ""} disabled={disabled || !v.country}
        autoComplete="address-level2" onBlur={blur("city")} placeholder={strict ? text("optional", "Optional") : ""}
        aria-invalid={errors.city ? "true" : "false"}
        onChange={(e) => { setCityInput(e.target.value); loc.setCity(e.target.value); }} />
    );
  } else if (strict) {
    cityControl = (
      <>
        <input id={id("city")} list={id("city-list")} value={cityInput} className={inputClass} autoComplete="off"
          disabled={disabled || !v.country || !loc.stateReady || loc.cities.loading}
          placeholder={loc.cities.loading ? text("loadingPlaces", "Loading…") : text("selectCity", "Select city (optional)")}
          onChange={(e) => { setCityInput(e.target.value); loc.setCity(e.target.value); }} />
        <datalist id={id("city-list")}>
          {loc.cities.items.map((c) => <option key={c.name} value={c.name} />)}
        </datalist>
      </>
    );
  } else {
    cityControl = (
      <>
        <input id={id("city")} list={id("city-list")} value={v.city || ""} className={inputClass}
          autoComplete="address-level2" disabled={disabled || !v.country} onBlur={blur("city")}
          aria-invalid={errors.city ? "true" : "false"}
          placeholder={loc.cities.loading ? text("loadingPlaces", "Loading…") : (loc.cities.items.length ? text("typeOrPickCity", "Type or pick a city") : "")}
          onChange={(e) => loc.setCity(e.target.value)} />
        <datalist id={id("city-list")}>
          {loc.cities.items.map((c) => <option key={c.name} value={c.name} />)}
        </datalist>
      </>
    );
  }

  // ── ZIP / postal code ──
  const postalControl = (
    <>
      <input id={id("zip")} list={loc.zipcodes.items.length ? id("zip-list") : undefined} value={loc.postalInput}
        className={inputClass} autoComplete={strict ? "off" : "postal-code"} disabled={disabled || !v.country || (strict && loc.zipcodes.loading)}
        onBlur={blur("postalCode")} aria-invalid={errors.postalCode || loc.mismatchStates.length ? "true" : "false"}
        aria-describedby={id("status")}
        placeholder={strict ? (loc.zipcodes.loading ? text("loadingPlaces", "Loading…") : text("selectZip", "Select ZIP / postal code (optional)")) : ""}
        onChange={(e) => loc.setPostalCode(e.target.value)} />
      {loc.zipcodes.items.length > 0 && (
        <datalist id={id("zip-list")}>
          {loc.zipcodes.items.map((z) => <option key={z.zipcode} value={z.zipcode} />)}
        </datalist>
      )}
    </>
  );

  // ── What happened with the code / the data ──
  const place = (m) => [m.city, m.state].filter(Boolean).join(", ");
  const status = [];
  const p = loc.postal;
  if (p.status === "pending" || p.status === "loading") {
    status.push(<p key="loading" className="alsel-msg alsel-msg--info">{text("lookingUp", "Looking up this code…")}</p>);
  } else if (p.status === "notFound") {
    status.push(<p key="nf" className="alsel-msg alsel-msg--warn">{strict
      ? text("zipNotFound", "This ZIP / postal code is not in our list for this country.")
      : text("postalNotFound", "No place found for this code. You can enter the city and the state yourself.")}</p>);
  } else if (p.status === "error" || loc.serviceDown) {
    status.push(<p key="err" className="alsel-msg alsel-msg--warn">{text("serviceDown", "The location service is not available right now. Enter the address by hand.")}</p>);
  }
  if (loc.noData && !loc.serviceDown) {
    status.push(<p key="nodata" className="alsel-msg alsel-msg--info">{strict
      ? text("noGeoData", "No list of places is available for this country: type the area (optional).")
      : text("noGeoDataAddress", "No list of places is available for this country: type the city and the region.")}</p>);
  }
  if (loc.mismatchStates.length) {
    status.push(<p key="mm" className="alsel-msg alsel-msg--warn" role="alert">{text("stateMismatch", "This code belongs to {states}. Check the state or the code.", { states: loc.mismatchStates.join(" / ") })}</p>);
  }
  if (loc.choices.length) {
    status.push(
      <div key="choices" className="alsel-choices" role="group" aria-label={text("choosePlace", "Several places use this code. Choose one:")}>
        <p className="alsel-msg alsel-msg--info">{text("choosePlace", "Several places use this code. Choose one:")}</p>
        <div className="alsel-choice-list">
          {loc.choices.map((m) => (
            <button key={`${m.city}|${m.state}`} type="button" className="alsel-choice" disabled={disabled}
              onClick={() => { setCityInput(m.city); loc.pickMatch(m); }}>{place(m)}</button>
          ))}
        </div>
      </div>
    );
  }
  if (loc.suggestion) {
    status.push(
      <p key="sugg" className="alsel-msg alsel-msg--info">
        {text("postalIsIn", "{code} is in {place}.", { code: loc.postalInput, place: place(loc.suggestion) })}{" "}
        <button type="button" className="alsel-link" disabled={disabled} onClick={() => loc.pickMatch(loc.suggestion)}>
          {text("useThisPlace", "Use this place")}
        </button>
      </p>
    );
  }
  if (!strict && p.status === "found" && !status.length && v.city && v.state) {
    status.push(<p key="ok" className="alsel-msg alsel-msg--ok">✓ {place({ city: v.city, state: loc.stateItem?.name || v.state })}</p>);
  }
  if (strict && !loc.freeText) {
    const cityUnknown = Boolean(cityInput) && !v.city && !loc.cities.loading && loc.cities.items.length > 0;
    if (cityUnknown) status.push(<small key="cnl" className="geo-cascade__error alsel-msg">{text("cityNotInList", "Choose a city from the list.")}</small>);
  }

  const statusBlock = (
    <div key="status" id={id("status")} className={cx("alsel-status", classNames.status)} aria-live="polite" hidden={!status.length}>
      {status}
    </div>
  );

  const countryField = field("country", countryLabel, countryControl);
  const stateField = field("state", stateLabel, stateControl, { showOptional: !strict && Boolean(v.country) && !loc.stateRequired });
  const cityField = field("city", cityLabel, cityControl);
  const postalField = field("postalCode", postalLabel, postalControl, { showOptional: !strict && Boolean(v.country) && !loc.postalRequired, idName: "zip" });

  const content = strict
    ? [countryField, stateField, cityField, postalField, statusBlock]
    : [countryField, postalField, statusBlock, stateField, cityField];

  return wrapperClassName ? <div className={wrapperClassName}>{content}</div> : <>{content}</>;
};

export default AddressLocationSelector;
